import React, { useState, useEffect, memo } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { detectSgtm, gtmAdviceSentence } from '../../utils/gtmAdvice';

const formatMs = (ms) => {
  if (ms === null || ms === undefined) return '-';
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.round(ms)}ms`;
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  if (bytes < k) return `${bytes} B`;
  if (bytes < k * k) return `${(bytes / k).toFixed(1)} KB`;
  return `${(bytes / (k * k)).toFixed(1)} MB`;
};

const DeviceCapabilities = memo(() => {
  const { language } = useLanguage();
  const [capabilities, setCapabilities] = useState(null);

  useEffect(() => {
    const detectCapabilities = () => {
      const caps = {
        deviceMemory: navigator.deviceMemory || null,
        hardwareConcurrency: navigator.hardwareConcurrency || null,
        connection: null,
        saveData: false
      };

      if ('connection' in navigator) {
        const conn = navigator.connection;
        caps.connection = {
          effectiveType: conn.effectiveType,
          downlink: conn.downlink,
          rtt: conn.rtt,
          saveData: conn.saveData
        };
        caps.saveData = conn.saveData;
      }

      setCapabilities(caps);
    };

    detectCapabilities();

    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', detectCapabilities);
      return () => navigator.connection.removeEventListener('change', detectCapabilities);
    }
  }, []);

  if (!capabilities) return null;

  const connectionLabels = {
    'slow-2g': { cs: 'Velmi pomalé (2G)', en: 'Very slow (2G)', color: 'text-red-400' },
    '2g': { cs: 'Pomalé (2G)', en: 'Slow (2G)', color: 'text-red-400' },
    '3g': { cs: 'Střední (3G)', en: 'Medium (3G)', color: 'text-amber-400' },
    '4g': { cs: 'Rychlé (4G/WiFi)', en: 'Fast (4G/WiFi)', color: 'text-green-400' }
  };

  const memoryLabels = {
    low: { cs: 'Omezená', en: 'Limited', threshold: 4 },
    medium: { cs: 'Střední', en: 'Medium', threshold: 8 },
    high: { cs: 'Dostatečná', en: 'Sufficient', threshold: Infinity }
  };

  const getMemoryLevel = (gb) => {
    if (!gb) return null;
    if (gb <= 4) return 'low';
    if (gb <= 8) return 'medium';
    return 'high';
  };

  const memoryLevel = getMemoryLevel(capabilities.deviceMemory);

  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-indigo-900/30 to-blue-900/30 border border-indigo-500/30">
      <div className="text-xs uppercase tracking-wider text-indigo-400 mb-3 flex items-center gap-2">
        <Icons.Monitor size={14} />
        {language === 'cs' ? 'Schopnosti zařízení' : 'Device Capabilities'}
        <span className="text-[10px] px-1.5 py-0.5 bg-indigo-500/20 rounded">W3C API</span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {capabilities.deviceMemory && (
          <div className="p-2 bg-slate-800/50 rounded">
            <div className="text-xs text-slate-400">{language === 'cs' ? 'Paměť' : 'Memory'}</div>
            <div className={`text-sm font-medium ${memoryLevel === 'low' ? 'text-amber-400' : 'text-white'}`}>
              {capabilities.deviceMemory} GB
            </div>
            <div className="text-[10px] text-slate-500">
              {memoryLabels[memoryLevel]?.[language]}
            </div>
          </div>
        )}
        
        {capabilities.hardwareConcurrency && (
          <div className="p-2 bg-slate-800/50 rounded">
            <div className="text-xs text-slate-400">{language === 'cs' ? 'CPU jádra' : 'CPU Cores'}</div>
            <div className="text-sm font-medium text-white">
              {capabilities.hardwareConcurrency}
            </div>
            <div className="text-[10px] text-slate-500">
              {capabilities.hardwareConcurrency >= 4 
                ? (language === 'cs' ? 'Vícejádrový' : 'Multi-core')
                : (language === 'cs' ? 'Omezený' : 'Limited')}
            </div>
          </div>
        )}
        
        {capabilities.connection && (
          <>
            <div className="p-2 bg-slate-800/50 rounded">
              <div className="text-xs text-slate-400">{language === 'cs' ? 'Připojení' : 'Connection'}</div>
              <div className={`text-sm font-medium ${connectionLabels[capabilities.connection.effectiveType]?.color || 'text-white'}`}>
                {connectionLabels[capabilities.connection.effectiveType]?.[language] || capabilities.connection.effectiveType}
              </div>
              {capabilities.connection.downlink && (
                <div className="text-[10px] text-slate-500">
                  {capabilities.connection.downlink} Mbps
                </div>
              )}
            </div>
            
            <div className="p-2 bg-slate-800/50 rounded">
              <div className="text-xs text-slate-400">RTT</div>
              <div className="text-sm font-medium text-white">
                {capabilities.connection.rtt || '-'} ms
              </div>
              <div className="text-[10px] text-slate-500">
                {language === 'cs' ? 'Latence sítě' : 'Network latency'}
              </div>
            </div>
          </>
        )}
      </div>
      
      {capabilities.saveData && (
        <div className="mt-2 p-2 bg-amber-500/20 rounded text-xs text-amber-300 flex items-center gap-2">
          <Icons.AlertTriangle size={12} />
          {language === 'cs' 
            ? 'Uživatel má zapnutý režim úspory dat'
            : 'User has data saver mode enabled'}
        </div>
      )}
    </div>
  );
});

const ServerTimingDisplay = memo(({ data }) => {
  const { language } = useLanguage();
  
  if (!data || (!data.breakdown && !data.timings?.length)) return null;

  const phases = data.breakdown;
  const total = phases?.total || data.responseTime || 0;
  
  const getTip = () => {
    if (!phases) return null;
    // All phases at 0 means the browser did not expose the timing breakdown
    // (cross-origin / cached navigation) — claiming "server is fast" here
    // would be a false OK based on missing data.
    const phaseSum = (phases.dns || 0) + (phases.connection || 0) + (phases.tls || 0) + (phases.waiting || 0) + (phases.receiving || 0);
    if (phaseSum <= 0) {
      return language === 'cs'
        ? 'Rozpad fází není k dispozici (prohlížeč data neposkytl) — rychlost serveru z tohoto měření nelze hodnotit.'
        : 'Phase breakdown unavailable (browser did not expose the data) — server speed cannot be judged from this measurement.';
    }
    if (language === 'cs') {
      if (phases.waiting > 200) return 'Vysoký TTFB (čas na první bajt) - server dlouho zpracovává požadavek. Optimalizujte backend, databázové dotazy nebo zvažte CDN/cache.';
      if (phases.phasesAvailable === false) return 'Doba odpovědi serveru (TTFB) je v normě. Fáze DNS/TCP/TLS nejsou z tohoto měření dostupné, takže je nelze hodnotit.';
      if (phases.dns > 100) return 'Pomalé DNS - použijte rychlý DNS provider (Cloudflare, Google DNS) nebo DNS prefetching.';
      if (phases.connection > 100) return 'Pomalé TCP spojení - server je geograficky daleko od uživatelů. Zvažte CDN blíže k cílové skupině.';
      return 'Serverová odpověď je rychlá. Všechny fáze jsou v normě.';
    } else {
      if (phases.waiting > 200) return 'High TTFB (Time to First Byte) - server takes long to respond. Optimize backend, database queries or consider CDN/cache.';
      if (phases.phasesAvailable === false) return 'Server response time (TTFB) is within range. DNS/TCP/TLS phases are not available from this measurement, so they cannot be evaluated.';
      if (phases.dns > 100) return 'Slow DNS - use fast DNS provider (Cloudflare, Google DNS) or DNS prefetching.';
      if (phases.connection > 100) return 'Slow TCP connection - server is geographically far from users. Consider CDN closer to target audience.';
      return 'Server response is fast. All phases are within normal range.';
    }
  };

  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-cyan-900/30 to-teal-900/30 border border-cyan-500/30">
      <div className="text-xs uppercase tracking-wider text-cyan-400 mb-2 flex items-center gap-2">
        <Icons.Server size={14} />
        {language === 'cs' ? 'Server Timing' : 'Server Timing'}
        <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 rounded">W3C</span>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {phases?.phasesAvailable === false
          ? (language === 'cs'
            ? 'Fáze DNS / TCP / TLS nejsou z tohoto měření (Lighthouse) dostupné — hodnotí se pouze doba odpovědi serveru (TTFB, jen serverová část bez DNS/TCP/TLS) a stažení dokumentu. Field TTFB z CrUX tyto fáze zahrnuje, proto bývá vyšší.'
            : 'DNS / TCP / TLS phases are not available from this measurement (Lighthouse) — only server response time (TTFB, server portion only, no DNS/TCP/TLS) and document download are evaluated. Field TTFB from CrUX includes those phases, so it tends to be higher.')
          : (language === 'cs'
            ? 'Rozklad doby odpovědi serveru: DNS lookup → TCP spojení → TLS handshake → čekání na odpověď → stahování.'
            : 'Server response time breakdown: DNS lookup → TCP connection → TLS handshake → waiting for response → download.')}
      </p>
      
      {phases && (
        <div className="mb-3">
          <div className="flex items-center gap-1 h-6 rounded overflow-hidden bg-slate-800">
            {phases.dns > 0 && (
              <div 
                className="h-full bg-blue-500 flex items-center justify-center text-[9px] text-white"
                style={{ width: `${Math.max((phases.dns / total) * 100, 5)}%` }}
                title={`DNS: ${formatMs(phases.dns)}`}
              >
                DNS
              </div>
            )}
            {phases.connection > 0 && (
              <div 
                className="h-full bg-purple-500 flex items-center justify-center text-[9px] text-white"
                style={{ width: `${Math.max((phases.connection / total) * 100, 5)}%` }}
                title={`Connect: ${formatMs(phases.connection)}`}
              >
                TCP
              </div>
            )}
            {phases.tls > 0 && (
              <div 
                className="h-full bg-green-500 flex items-center justify-center text-[9px] text-white"
                style={{ width: `${Math.max((phases.tls / total) * 100, 5)}%` }}
                title={`TLS: ${formatMs(phases.tls)}`}
              >
                TLS
              </div>
            )}
            {phases.waiting > 0 && (
              <div 
                className="h-full bg-amber-500 flex items-center justify-center text-[9px] text-white"
                style={{ width: `${Math.max((phases.waiting / total) * 100, 10)}%` }}
                title={`TTFB: ${formatMs(phases.waiting)}`}
              >
                TTFB
              </div>
            )}
            {phases.receiving > 0 && (
              <div 
                className="h-full bg-cyan-500 flex items-center justify-center text-[9px] text-white"
                style={{ width: `${Math.max((phases.receiving / total) * 100, 5)}%` }}
                title={`Download: ${formatMs(phases.receiving)}`}
              >
                DL
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-5 gap-1 mt-2 text-[10px]">
            <div className="text-center">
              <span className="inline-block w-2 h-2 bg-blue-500 rounded mr-1"></span>
              <span className="text-slate-400">DNS</span>
              <div className="text-white">{formatMs(phases.dns)}</div>
            </div>
            <div className="text-center">
              <span className="inline-block w-2 h-2 bg-purple-500 rounded mr-1"></span>
              <span className="text-slate-400">TCP</span>
              <div className="text-white">{formatMs(phases.connection)}</div>
            </div>
            <div className="text-center">
              <span className="inline-block w-2 h-2 bg-green-500 rounded mr-1"></span>
              <span className="text-slate-400">TLS</span>
              <div className="text-white">{formatMs(phases.tls)}</div>
            </div>
            <div className="text-center">
              <span className="inline-block w-2 h-2 bg-amber-500 rounded mr-1"></span>
              <span className="text-slate-400">TTFB</span>
              <div className="text-white">{formatMs(phases.waiting)}</div>
            </div>
            <div className="text-center">
              <span className="inline-block w-2 h-2 bg-cyan-500 rounded mr-1"></span>
              <span className="text-slate-400">DL</span>
              <div className="text-white">{formatMs(phases.receiving)}</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="text-xs text-slate-400 text-right mb-3">
        {language === 'cs' ? 'Celkem:' : 'Total:'} <span className="text-white font-medium">{formatMs(total)}</span>
      </div>
      
      {getTip() && (
        <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
          <div className="text-xs font-medium text-cyan-400 mb-1 flex items-center gap-1">
            <Icons.Lightbulb size={12} />
            {language === 'cs' ? 'Tip:' : 'Tip:'}
          </div>
          <p className="text-xs text-gray-300">{getTip()}</p>
        </div>
      )}
    </div>
  );
});

const LongTasksDisplay = memo(({ data, diagnostics }) => {
  const { language } = useLanguage();
  
  if (!data || data.count === 0) return null;

  const severityColors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    low: 'bg-green-500/20 text-green-400 border-green-500/30'
  };
  
  const getSmartTips = () => {
    const tips = [];
    const tasks = data.tasks || [];
    const tbt = data.totalBlockingTime || 0;

    const gtmTasks = tasks.filter(t => /googletagmanager\.com\/gtm\.js\?[^#]*\bid=GTM-/i.test(t.url || ''));
    const googleTagTasks = tasks.filter(t => /googletagmanager\.com\/gtag\/(?:js|destination)(?:[?#]|$)/i.test(t.url || ''));
    const adTasks = tasks.filter(t => t.url?.includes('doubleclick') || t.url?.includes('googlesyndication') || t.url?.includes('adservice') || t.url?.includes('pagead'));
    const trackerTasks = tasks.filter(t => t.url?.includes('tracker') || t.url?.includes('analytics') || t.url?.includes('hotjar') || t.url?.includes('clarity'));
    const thirdPartyTasks = [...gtmTasks, ...googleTagTasks, ...adTasks, ...trackerTasks];
    const firstPartyTasks = tasks.filter(t => !thirdPartyTasks.includes(t));

    if (gtmTasks.length > 0) {
      const totalGtmBlock = gtmTasks.reduce((s, t) => s + (t.blockingTime || 0), 0);
      // Detection + advice wording come from the shared gtmAdvice module —
      // the SAME source the HTML export uses, so the dashboard can never
      // contradict the exported report ("deploy sGTM" vs. "finish the
      // migration"). Do not inline GTM copy here.
      const sgtmDetected = detectSgtm(diagnostics);
      const advice = gtmAdviceSentence(sgtmDetected, language === 'cs' ? 'cs' : 'en');
      if (language === 'cs') {
        tips.push(`Google Tag Manager generuje ${gtmTasks.length} dlouh${gtmTasks.length === 1 ? 'ou úlohu' : gtmTasks.length < 5 ? 'é úlohy' : 'ých úloh'} (+${totalGtmBlock}ms blokování). ${advice} Méně důležité tagy přesuňte do sekce "po interakci" s triggerem Window Loaded.`);
      } else {
        tips.push(`Google Tag Manager generates ${gtmTasks.length} long task${gtmTasks.length > 1 ? 's' : ''} (+${totalGtmBlock}ms blocking). ${advice} Defer non-critical tags with the Window Loaded trigger.`);
      }
    }
    if (googleTagTasks.length > 0) {
      const block = googleTagTasks.reduce((s, t) => s + (t.blockingTime || 0), 0);
      tips.push(language === 'cs'
        ? `Google tag / destinace má ${googleTagTasks.length} dlouhých úloh (+${block} ms blokování); požadavky gtag ani destinace nedokládají GTM kontejner. Prověřte načítání tagů a ověřte dopad změn měřením.`
        : `Google tag / destinations have ${googleTagTasks.length} long tasks (+${block} ms blocking); neither gtag nor destination requests establish a GTM container. Review tag loading and measure the impact of changes.`);
    }

    if (adTasks.length > 0) {
      const totalAdBlock = adTasks.reduce((s, t) => s + (t.blockingTime || 0), 0);
      if (language === 'cs') {
        tips.push(`Reklamní skripty blokují hlavní vlákno o ${totalAdBlock}ms. Řešení: Načtěte reklamy až po prvním vykreslení (lazy-load pod fold), použijte atribut loading="lazy" na reklamní iframy.`);
      } else {
        tips.push(`Ad scripts block the main thread by ${totalAdBlock}ms. Fix: Load ads after first render (lazy-load below fold), use loading="lazy" attribute on ad iframes.`);
      }
    }

    if (trackerTasks.length > 0) {
      const worstTracker = trackerTasks.sort((a, b) => (b.blockingTime || 0) - (a.blockingTime || 0))[0];
      const domain = (() => { try { return new URL(worstTracker.url).hostname; } catch { return worstTracker.url; } })();
      if (language === 'cs') {
        tips.push(`Analytický/tracking skript z ${domain} blokuje vlákno o ${worstTracker.blockingTime}ms. Řešení: Načtěte tracking skripty asynchronně s defer, nebo zvažte odlehčenou alternativu.`);
      } else {
        tips.push(`Analytics/tracking script from ${domain} blocks thread by ${worstTracker.blockingTime}ms. Fix: Load tracking scripts asynchronously with defer, or consider a lighter alternative.`);
      }
    }

    if (firstPartyTasks.length > 0) {
      const worstOwn = firstPartyTasks.sort((a, b) => (b.duration || 0) - (a.duration || 0))[0];
      if (worstOwn && worstOwn.duration > 100) {
        const domain = (() => { try { return new URL(worstOwn.url).hostname; } catch { return ''; } })();
        if (language === 'cs') {
          tips.push(`Vlastní skript${domain ? ` (${domain})` : ''} běží ${worstOwn.duration}ms. Řešení: Rozdělte ho na menší části pomocí scheduler.yield() nebo requestIdleCallback(), přesuňte těžké výpočty do Web Worker.`);
        } else {
          tips.push(`First-party script${domain ? ` (${domain})` : ''} runs for ${worstOwn.duration}ms. Fix: Break it into smaller chunks using scheduler.yield() or requestIdleCallback(), move heavy computation to Web Worker.`);
        }
      }
    }

    if (tips.length === 0) {
      if (tbt > 2000) {
        tips.push(language === 'cs'
          ? 'Kritický problém: Dlouhé úlohy blokují hlavní vlákno přes 2 sekundy. Rozdělte JavaScript kód pomocí code-splitting, použijte Web Workers pro těžké výpočty.'
          : 'Critical issue: Long tasks block main thread over 2 seconds. Split JavaScript with code-splitting, use Web Workers for heavy computations.');
      } else if (tbt > 500) {
        tips.push(language === 'cs'
          ? 'JavaScript blokuje interaktivitu. Rozdělte dlouhé úlohy pomocí scheduler.yield() nebo requestIdleCallback(), lazy-loadujte nekritické skripty.'
          : 'JavaScript blocks interactivity. Split long tasks with scheduler.yield() or requestIdleCallback(), lazy-load non-critical scripts.');
      } else {
        tips.push(language === 'cs'
          ? `Nalezeno ${data.count} dlouh${data.count === 1 ? 'á úloha' : data.count < 5 ? 'é úlohy' : 'ých úloh'} s celkovým blokováním ${Math.round(tbt)}ms. Stránka reaguje relativně dobře, ale pro další zlepšení zvažte odložené načítání nekritických skriptů.`
          : `Found ${data.count} long task${data.count > 1 ? 's' : ''} with total blocking of ${Math.round(tbt)}ms. Page responds relatively well, but consider deferring non-critical scripts for further improvement.`);
      }
    }

    return tips;
  };

  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/30">
      <div className="text-xs uppercase tracking-wider text-red-400 mb-2 flex items-center gap-2">
        <Icons.Clock size={14} />
        {language === 'cs' ? 'Dlouhé úlohy (>50ms)' : 'Long Tasks (>50ms)'}
        <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 rounded">W3C</span>
        <span className="ml-auto text-white font-medium">{data.count}×</span>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {language === 'cs' 
          ? 'Úlohy trvající déle než 50ms blokují hlavní vlákno a zpomalují reakci na kliknutí (INP). Čím méně dlouhých úloh, tím lepší odezva.' 
          : 'Tasks longer than 50ms block the main thread and slow click response (INP). Fewer long tasks mean better responsiveness.'}
      </p>
      
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="p-2 bg-slate-800/50 rounded text-center">
          <div className="text-lg font-bold text-white">{formatMs(data.totalBlockingTime)}</div>
          <div className="text-[10px] text-slate-400">
            {language === 'cs' ? 'Total Blocking Time' : 'Total Blocking Time'}
          </div>
        </div>
        
        {data.impactOnINP && (
          <div className={`p-2 rounded text-center border ${severityColors[data.impactOnINP.severity]}`}>
            <div className="text-lg font-bold">{formatMs(data.impactOnINP.estimatedDelay)}</div>
            <div className="text-[10px]">
              {language === 'cs' ? 'Průměrné zpoždění' : 'Avg delay'}
            </div>
          </div>
        )}
      </div>
      
      {data.tasks?.length > 0 && (
        <div className="space-y-1 mb-3">
          {data.tasks.slice(0, 5).map((task, i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-slate-800/30 rounded text-xs">
              <div className="w-16 text-right text-red-400 font-mono">{task.duration}ms</div>
              <div className="flex-1 text-slate-300 break-all text-[10px] leading-tight" title={task.url}>
                {task.url}
              </div>
              <div className="text-slate-500">+{task.blockingTime}ms block</div>
            </div>
          ))}
        </div>
      )}
      
      <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
        <div className="text-xs font-medium text-red-400 mb-1 flex items-center gap-1">
          <Icons.Lightbulb size={12} />
          {language === 'cs' ? 'Doporučení:' : 'Recommendation:'}
        </div>
        <div className="space-y-2">
          {getSmartTips().map((tip, i) => (
            <p key={i} className="text-xs text-gray-300">{tip}</p>
          ))}
        </div>
      </div>
    </div>
  );
});

const ResourceTimingDisplay = memo(({ data }) => {
  const { language } = useLanguage();
  
  if (!data || (Object.keys(data.summary).length === 0 && data.slowestResources?.length === 0)) return null;
  
  const getTip = () => {
    const thirdParty = data.thirdPartyImpact;
    const slowest = data.slowestResources?.[0];
    const slowDomain = slowest?.name ? (() => { try { return new URL(slowest.name).hostname; } catch { return slowest.name; } })() : null;
    if (language === 'cs') {
      if (thirdParty?.count > 50) return `Příliš mnoho 3rd-party zdrojů (${thirdParty.count}). Každý zdroj navíc zpomaluje načítání. Auditujte nutnost každého a odstraňte zbytečné.`;
      if (thirdParty?.totalSize > 500000) return `3rd-party zdroje přenášejí ${formatBytes(thirdParty.totalSize)}. Zvažte self-hosting kritických knihoven nebo jejich odložené načítání.`;
      if (slowest) return `Nejpomalejší zdroj: ${slowDomain || slowest.name} (${formatMs(slowest.duration)}). Přidejte <link rel="preconnect"> pro tuto doménu a optimalizujte velikost souboru pomocí komprese nebo moderních formátů.`;
      return 'Načítání zdrojů je v normě. Všechny zdroje se stahují v přijatelném čase.';
    } else {
      if (thirdParty?.count > 50) return `Too many 3rd-party resources (${thirdParty.count}). Each additional resource slows loading. Audit necessity and remove unnecessary ones.`;
      if (thirdParty?.totalSize > 500000) return `3rd-party resources transfer ${formatBytes(thirdParty.totalSize)}. Consider self-hosting critical libraries or lazy loading.`;
      if (slowest) return `Slowest resource: ${slowDomain || slowest.name} (${formatMs(slowest.duration)}). Add <link rel="preconnect"> for this domain and optimize file size with compression or modern formats.`;
      return 'Resource loading is normal. All resources download within acceptable time.';
    }
  };

  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30">
      <div className="text-xs uppercase tracking-wider text-purple-400 mb-2 flex items-center gap-2">
        <Icons.Layers size={14} />
        {language === 'cs' ? 'Resource Timing' : 'Resource Timing'}
        <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 rounded">W3C</span>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {language === 'cs' 
          ? 'Přehled všech zdrojů stránky podle typu. 3rd-party zdroje jsou z externích domén a mohou významně ovlivnit rychlost.' 
          : 'Overview of all page resources by type. 3rd-party resources are from external domains and can significantly impact speed.'}
      </p>
      
      {Object.keys(data.summary).length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
          {Object.entries(data.summary).slice(0, 6).map(([type, info]) => {
            {/* Lighthouse Resource Summary's own 'third-party' category — a
                different classification than the cross-hostname network-log
                count below; label the source so the two aren't read as one metric. */}
            const isTpRow = String(type).toLowerCase() === 'third-party';
            const label = isTpRow ? `${type} (LH Resource Summary)` : type;
            return (
              <div key={type} className="p-2 bg-slate-800/50 rounded text-center">
                <div className="text-sm font-medium text-white">{info.count}</div>
                <div className="text-[10px] text-slate-400 truncate" title={isTpRow ? 'third-party dle Lighthouse Resource Summary — jiná klasifikace než počet požadavků na jiné hostname níže' : type}>{label}</div>
                <div className="text-[10px] text-slate-500">{formatBytes(info.size)}</div>
              </div>
            );
          })}
        </div>
      )}
      
      {data.thirdPartyImpact.count > 0 && (
        <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded mb-3">
          <div className="text-xs text-amber-300 mb-1">
            {language === 'cs' ? 'Požadavky na jiné hostname (síťový záznam)' : 'Requests to other hostnames (network log)'}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
            <div>
              <div className="text-white font-medium">{data.thirdPartyImpact.count}</div>
              <div className="text-slate-400">{language === 'cs' ? 'požadavků' : 'requests'}</div>
            </div>
            <div>
              <div className="text-white font-medium">{formatBytes(data.thirdPartyImpact.totalSize)}</div>
              <div className="text-slate-400">{language === 'cs' ? 'dat' : 'data'}</div>
            </div>
            <div>
              <div className="text-white font-medium">{formatMs(data.thirdPartyImpact.totalTime)}</div>
              <div className="text-slate-400">{language === 'cs' ? 'čas' : 'time'}</div>
            </div>
          </div>
          <div className="text-[9px] text-slate-500 mt-1">
            {language === 'cs'
              ? 'Měřeno ze síťového záznamu — jiná metodika než souhrn podle typu výše, počty nelze přímo porovnávat. „Cizí doména" = jiný hostname; může zahrnovat i vlastní CDN webu.'
              : 'Measured from the network log — a different methodology than the per-type summary above; counts are not directly comparable. "Third-party domain" = different hostname; it may include the site\'s own CDN.'}
          </div>
        </div>
      )}
      
      {data.slowestResources?.length > 0 && (
        <div>
          <div className="text-[10px] text-slate-400 mb-1">
            {language === 'cs' ? 'Nejpomalejší zdroje:' : 'Slowest resources:'}
          </div>
          <div className="space-y-1">
            {data.slowestResources.slice(0, 4).map((res, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px] p-1 bg-slate-800/30 rounded">
                <span className="text-purple-400 w-12 text-right">{formatMs(res.duration)}</span>
                <span className="text-slate-500 w-12">{res.type}</span>
                <span className="text-slate-300 break-all flex-1" title={res.url}>
                  {res.url}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
        <div className="text-xs font-medium text-purple-400 mb-1 flex items-center gap-1">
          <Icons.Lightbulb size={12} />
          {language === 'cs' ? 'Tip:' : 'Tip:'}
        </div>
        <p className="text-xs text-gray-300">{getTip()}</p>
      </div>
    </div>
  );
});

const MainThreadDisplay = memo(({ data }) => {
  const { language } = useLanguage();
  
  if (!data || data.total === 0) return null;

  const categoryLabels = {
    scriptEvaluation: { cs: 'Skripty', en: 'Scripts', color: 'bg-yellow-500' },
    scriptParseCompile: { cs: 'Parse/Compile', en: 'Parse/Compile', color: 'bg-orange-500' },
    styleLayout: { cs: 'Styly', en: 'Styles', color: 'bg-purple-500' },
    paintCompositeRender: { cs: 'Render', en: 'Render', color: 'bg-green-500' },
    garbageCollection: { cs: 'GC', en: 'GC', color: 'bg-red-500' },
    parseHTML: { cs: 'HTML', en: 'HTML', color: 'bg-blue-500' },
    other: { cs: 'Ostatní', en: 'Other', color: 'bg-slate-500' }
  };
  
  const getTip = () => {
    const scriptCat = data.categories?.find(c => c.category === 'scriptEvaluation');
    const layoutCat = data.categories?.find(c => c.category === 'styleLayout');
    const scriptPct = scriptCat?.percentage || 0;
    const layoutPct = layoutCat?.percentage || 0;
    const scriptTime = scriptCat?.duration || 0;
    const layoutTime = layoutCat?.duration || 0;
    if (language === 'cs') {
      if (data.total > 10000) return `Kriticky vysoká zátěž hlavního vlákna (${formatMs(data.total)}). Hlavní viník: ${scriptPct > layoutPct ? `JavaScript (${formatMs(scriptTime)}, ${scriptPct}%)` : `styly a layout (${formatMs(layoutTime)}, ${layoutPct}%)`}. Priorita: zmenšit JavaScript bundle, odložit nekritické skripty, minimalizovat reflow.`;
      if (scriptPct > 60) return `JavaScript zabírá ${scriptPct}% času hlavního vlákna (${formatMs(scriptTime)}). Zmenšete velikost bundle pomocí tree-shaking a code-splitting, lazy-loadujte nekritické knihovny.`;
      if (layoutPct > 30) return `Styly a layout zabírají ${layoutPct}% času (${formatMs(layoutTime)}). Zmenšete CSS, vyhněte se vynuceným reflow (čtení offsetHeight po zápisu stylu), použijte CSS containment.`;
      if (data.total > 4000) return `Vysoká zátěž vlákna (${formatMs(data.total)}). Zkontrolujte, které skripty jsou potřeba při prvním načtení a zbytek odložte pomocí defer/async.`;
      return `Práce vlákna je v pořádku (${formatMs(data.total)}). Stránka by měla reagovat rychle na uživatelské akce.`;
    } else {
      if (data.total > 10000) return `Critically high main thread work (${formatMs(data.total)}). Main culprit: ${scriptPct > layoutPct ? `JavaScript (${formatMs(scriptTime)}, ${scriptPct}%)` : `style & layout (${formatMs(layoutTime)}, ${layoutPct}%)`}. Priority: reduce JavaScript bundle, defer non-critical scripts, minimize reflow.`;
      if (scriptPct > 60) return `JavaScript takes ${scriptPct}% of main thread time (${formatMs(scriptTime)}). Reduce bundle size with tree-shaking and code-splitting, lazy-load non-critical libraries.`;
      if (layoutPct > 30) return `Style & layout takes ${layoutPct}% of time (${formatMs(layoutTime)}). Reduce CSS, avoid forced reflows (reading offsetHeight after style writes), use CSS containment.`;
      if (data.total > 4000) return `High thread work (${formatMs(data.total)}). Review which scripts are needed on first load and defer the rest with defer/async.`;
      return `Thread work is good (${formatMs(data.total)}). Page should respond quickly to user actions.`;
    }
  };

  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-amber-500/30">
      <div className="text-xs uppercase tracking-wider text-amber-400 mb-2 flex items-center gap-2">
        <Icons.Cpu size={14} />
        {language === 'cs' ? 'Práce hlavního vlákna' : 'Main Thread Work'}
        <span className="ml-auto text-white">{formatMs(data.total)}</span>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {language === 'cs' 
          ? 'Rozložení práce, kterou prohlížeč vykonává při načítání stránky. Vysoký podíl skriptů zpomaluje interaktivitu.' 
          : 'Breakdown of work browser does when loading page. High script share slows interactivity.'}
      </p>
      
      <div className="h-4 rounded-full overflow-hidden flex bg-slate-800 mb-2">
        {data.categories.map((cat, i) => {
          const style = categoryLabels[cat.category] || categoryLabels.other;
          return (
            <div
              key={i}
              className={`${style.color} transition-all`}
              style={{ width: `${cat.percentage}%` }}
              title={`${style[language]}: ${formatMs(cat.duration)} (${cat.percentage}%)`}
            />
          );
        })}
      </div>
      
      <div className="grid grid-cols-3 md:grid-cols-6 gap-1 text-[10px]">
        {data.categories.slice(0, 6).map((cat, i) => {
          const style = categoryLabels[cat.category] || categoryLabels.other;
          return (
            <div key={i} className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded ${style.color}`}></span>
              <span className="text-slate-400 truncate">{style[language]}</span>
              <span className="text-white ml-auto">{cat.percentage}%</span>
            </div>
          );
        })}
      </div>
      
      {data.topScripts?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-amber-500/20">
          <div className="text-[10px] text-slate-400 mb-1">
            {language === 'cs' ? 'Nejnáročnější skripty:' : 'Top scripts:'}
          </div>
          {data.topScripts.slice(0, 3).map((script, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] py-1">
              <span className="text-amber-400 w-12 text-right">{formatMs(script.total)}</span>
              <span className="text-slate-300 break-all flex-1" title={script.url}>
                {script.url}
              </span>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
        <div className="text-xs font-medium text-amber-400 mb-1 flex items-center gap-1">
          <Icons.Lightbulb size={12} />
          {language === 'cs' ? 'Doporučení:' : 'Recommendation:'}
        </div>
        <p className="text-xs text-gray-300">{getTip()}</p>
      </div>
    </div>
  );
});

export const W3CPerformanceInsights = memo(({ diagnostics, showDeviceCapabilities = true }) => {
  const { language } = useLanguage();
  
  if (!diagnostics) return null;
  
  const hasData = diagnostics.serverTiming || diagnostics.longTasks?.count > 0 || 
                  diagnostics.resourceTiming || diagnostics.mainThreadBreakdown?.total > 0;
  
  if (!hasData && !showDeviceCapabilities) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Icons.Activity size={16} className="text-cyan-400" />
        <h3 className="text-sm font-medium text-white">
          {language === 'cs' ? 'Detailní analýza výkonu' : 'Detailed Performance Analysis'}
        </h3>
      </div>
      
      {showDeviceCapabilities && <DeviceCapabilities />}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ServerTimingDisplay data={diagnostics.serverTiming} />
        <LongTasksDisplay data={diagnostics.longTasks} diagnostics={diagnostics} />
        <ResourceTimingDisplay data={diagnostics.resourceTiming} />
        <MainThreadDisplay data={diagnostics.mainThreadBreakdown} />
      </div>
    </div>
  );
});

export default W3CPerformanceInsights;
